import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { useAppData } from '../data'
import { canonicalBillPath } from '../discovery'
import { relativeTimeLabel } from '../helpers'
import { BillSearchResults, hasMeaningfulQuery, lookupResults } from './search'

// HeaderSearch is only rendered when not on the home page.
function HeaderSearch() {
  const app = useAppData()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const results = useMemo(() => {
    if (app.billsIndex.status !== 'ready') return []
    return lookupResults(app.billsIndex.data.bills, query, 8)
  }, [app.billsIndex, query])

  const onSelect = (boletin: string) => {
    setQuery('')
    setOpen(false)
    navigate(canonicalBillPath(boletin))
  }

  return (
    <div
      className="global-search"
      onBlur={() => {
        if (timerRef.current) clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => {
          if (!document.activeElement?.closest('.global-search')) {
            setOpen(false)
          }
        }, 0)
      }}
    >
      <input
        type="text"
        placeholder="Buscar boletín, tema, autor o ley..."
        value={query}
        onFocus={() => {
          app.triggerBillsIndex()
          setOpen(hasMeaningfulQuery(query))
        }}
        onChange={(ev) => {
          const value = ev.target.value
          setQuery(value)
          setOpen(hasMeaningfulQuery(value))
        }}
      />
      {open && results.length > 0 && (
        <BillSearchResults
          results={results}
          onSelect={onSelect}
          containerClass="global-search-panel"
          itemClass="global-search-result"
        />
      )}
    </div>
  )
}

export function Header() {
  const location = useLocation()
  const [lastUpdated, setLastUpdated] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    fetch('/data/stats.json', { signal: controller.signal })
      .then((r) => r.json())
      .then((json: unknown) => {
        if (
          json != null &&
          typeof json === 'object' &&
          'last_updated' in json &&
          typeof (json as Record<string, unknown>).last_updated === 'string'
        ) {
          setLastUpdated((json as Record<string, string>).last_updated)
        }
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        // tolerate failure — leave lastUpdated as null
      })
    return () => controller.abort()
  }, [])

  const badgeInfo = useMemo(() => {
    if (lastUpdated == null) return null
    const now = new Date()
    const [label, recent] = relativeTimeLabel(
      lastUpdated,
      now.getFullYear(),
      now.getMonth() + 1,
      now.getDate(),
    )
    const dotClass = recent ? 'pulse-dot recent' : 'pulse-dot stale'
    return { label, dotClass }
  }, [lastUpdated])

  return (
    <header className="site-header site-header--nav">
      <div className="header-inner">
        <Link to="/" className="brand">
          <div className="brand-shield">
            <svg viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="2" width="40" height="40" rx="10" fill="#143060" stroke="#2460B8" strokeWidth="2" />
              <path d="M10 24 A13 13 0 0 1 34 24" fill="none" stroke="white" strokeWidth="2" opacity="0.35" />
              <path d="M14 24 A9 9 0 0 1 30 24" fill="none" stroke="white" strokeWidth="2" opacity="0.6" />
              <path d="M18 24 A5 5 0 0 1 26 24" fill="none" stroke="white" strokeWidth="2" opacity="0.9" />
              <rect x="20" y="26" width="4" height="2" rx="0.5" fill="#C8323F" />
            </svg>
          </div>
          <div className="brand-text">
            <span className="brand-title">Hemiciclo</span>
            <span className="brand-subtitle">Chile</span>
            <span className="brand-periodo">2026{'–'}2030</span>
          </div>
        </Link>
        <div className="header-status">
          <span className="badge-independiente">Independiente</span>
          {badgeInfo != null && (
            <span className="badge-updated">
              <span className={badgeInfo.dotClass}></span>
              {badgeInfo.label}
            </span>
          )}
        </div>
        {location.pathname !== '/' && <HeaderSearch />}
      </div>
    </header>
  )
}
